import { ContentLibrary, ContentItem } from '../components/ContentLibrary'
import { ContentEditForm } from '../components/ContentEditForm'
import { ContentDeleteDialog } from '../components/ContentDeleteDialog'
import { useState } from 'react'
import { motion } from 'framer-motion'
import { X } from 'lucide-react'

export function ContentLibraryPage() {
  const [selectedContent, setSelectedContent] = useState<ContentItem | null>(null)
  const [editingContent, setEditingContent] = useState<ContentItem | null>(null)
  const [deletingContent, setDeletingContent] = useState<ContentItem | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)

  const userStr = localStorage.getItem('user')
  const user = userStr ? JSON.parse(userStr) : null
  const canEdit = user?.role === 'company_admin' || user?.role === 'super_admin'

  const handleContentClick = (content: ContentItem) => {
    setSelectedContent(content)
  }

  const handleEdit = (content: ContentItem) => {
    setEditingContent(content)
  }

  const handleDelete = (content: ContentItem) => {
    setDeletingContent(content)
  }

  const handleCloseModal = () => {
    setSelectedContent(null)
  }

  const handleSaveEdit = async (id: string, data: Partial<ContentItem>) => {
    const token = localStorage.getItem('token')
    const response = await fetch(`http://localhost:3000/api/content/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(data)
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to update content')
    }

    // Refresh the content library
    setRefreshKey(prev => prev + 1)
  }

  const handleConfirmDelete = async (id: string) => {
    const token = localStorage.getItem('token')
    const response = await fetch(`http://localhost:3000/api/content/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to delete content')
    }

    // Refresh the content library
    setRefreshKey(prev => prev + 1)
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <ContentLibrary
        key={refreshKey}
        onContentClick={handleContentClick}
        onEdit={canEdit ? handleEdit : undefined}
        onDelete={canEdit ? handleDelete : undefined}
      />

      {/* Content Detail Modal */}
      {selectedContent && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={handleCloseModal}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-card rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between">
              <h2 className="text-xl font-bold">{selectedContent.title}</h2>
              <button
                onClick={handleCloseModal}
                className="p-2 hover:bg-secondary rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              {/* Thumbnail */}
              {selectedContent.thumbnailUrl && (
                <div className="rounded-lg overflow-hidden bg-secondary">
                  <img
                    src={`http://localhost:3000${selectedContent.thumbnailUrl}`}
                    alt={selectedContent.title}
                    className="w-full"
                  />
                </div>
              )}

              {/* Description */}
              {selectedContent.description && (
                <div>
                  <h3 className="font-semibold mb-2">Description</h3>
                  <p className="text-muted-foreground">{selectedContent.description}</p>
                </div>
              )}

              {/* Metadata */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="font-semibold mb-2">Type</h3>
                  <p className="text-muted-foreground capitalize">{selectedContent.type}</p>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Access Level</h3>
                  <p className="text-muted-foreground capitalize">{selectedContent.accessLevel}</p>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Created</h3>
                  <p className="text-muted-foreground">
                    {new Date(selectedContent.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Last Updated</h3>
                  <p className="text-muted-foreground">
                    {new Date(selectedContent.updatedAt).toLocaleDateString()}
                  </p>
                </div>
              </div>

              {/* Tags */}
              {selectedContent.metadata?.tags && selectedContent.metadata.tags.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-2">Tags</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedContent.metadata.tags.map((tag: string, index: number) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-secondary text-secondary-foreground rounded-full text-sm"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 pt-4">
                <a
                  href={`http://localhost:3000${selectedContent.filePath}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-center font-medium"
                >
                  View File
                </a>
                <button
                  className="px-4 py-2 border border-border rounded-lg hover:bg-secondary transition-colors"
                >
                  Download
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Edit Form Modal */}
      <ContentEditForm
        content={editingContent}
        isOpen={!!editingContent}
        onClose={() => setEditingContent(null)}
        onSave={handleSaveEdit}
      />

      {/* Delete Confirmation Dialog */}
      <ContentDeleteDialog
        content={deletingContent}
        isOpen={!!deletingContent}
        onClose={() => setDeletingContent(null)}
        onDelete={handleConfirmDelete}
      />
    </div>
  )
}
